import { useState, useEffect, useRef, useMemo } from "react";
import * as LC from 'lightweight-charts';

var API = 'https://api.algrok.com';
var LOT = 65;

function fmt(v, lots) {
  if (v == null || isNaN(v)) return '--';
  var x = lots ? v / LOT : v;
  var a = Math.abs(x);
  if (a >= 1e7) return (x / 1e7).toFixed(2) + 'Cr';
  if (a >= 1e5) return (x / 1e5).toFixed(2) + 'L';
  if (a >= 1e3) return (x / 1e3).toFixed(1) + 'k';
  return '' + Math.round(x);
}
function fmtSigned(v, lots) {
  if (v == null || isNaN(v) || v === 0) return '0';
  var s = v < 0 ? '−' : '+';
  return s + fmt(Math.abs(v), lots);
}

var WINDOWS = [
  { w: 3, label: '3 min' }, { w: 5, label: '5 min' }, { w: 10, label: '10 min' },
  { w: 15, label: '15 min' }, { w: 30, label: '30 min' }, { w: 60, label: '1 hr' },
  { w: 120, label: '2 hr' }, { w: 180, label: '3 hr' }, { w: 0, label: 'All' },
];
var RANGES = [
  { r: 'all', label: 'All' }, { r: 5, label: '5' }, { r: 10, label: '10' }, { r: 20, label: '20' },
];

function FlowCandles(props) {
  var lots = props.lots;
  var chg20 = props.chg20;
  var [data, setData] = useState(null);
  var [tf, setTf] = useState(5);
  var [band, setBand] = useState(10);
  var [mode, setMode] = useState('flow');
  var wrapRef = useRef(null);
  var roRef = useRef(null);
  var chartRef = useRef(null);
  var seriesRef = useRef(null);
  var liveRef = useRef({ spot: 0, cndl: null });
  var histRef = useRef({ tf: null, bars: [] });
  var lastFixRef = useRef(null);
  var [histLoading, setHistLoading] = useState(false);
  var [logScale, setLogScale] = useState(false);
  var [cd, setCd] = useState(null); // { txt, y } candle-close countdown
  var byTimeRef = useRef({});
  var lotsRef = useRef(lots);
  lotsRef.current = lots;
  var dataRef = useRef(null);
  dataRef.current = data;

  function fmtL(v) {
    if (v == null || isNaN(v)) return '--';
    var x = lotsRef.current ? v / 65 : v;
    var a = Math.abs(x);
    if (a >= 1e7) return (x / 1e7).toFixed(2) + 'Cr';
    if (a >= 1e5) return (x / 1e5).toFixed(1) + 'L';
    if (a >= 1e3) return (x / 1e3).toFixed(0) + 'k';
    return String(Math.round(x));
  }
  function bucketTime(hhmm) {
    // Encode IST wall time as UTC so the chart's time axis displays it verbatim.
    var now = new Date();
    var h = parseInt(hhmm.slice(0, 2), 10), m = parseInt(hhmm.slice(3, 5), 10);
    return Math.floor(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate(), h, m) / 1000);
  }

  // ── chart construction (once per mount) ──
  useEffect(function () {
    var el = wrapRef.current;
    if (!el) return;
    var chart = LC.createChart(el, {
      autoSize: true,
      layout: { background: { color: 'transparent' }, textColor: '#8b98a9', fontSize: 10, panes: { separatorColor: '#1f2937' } },
      grid: { vertLines: { color: 'rgba(31,41,55,0.5)' }, horzLines: { color: 'rgba(31,41,55,0.5)' } },
      timeScale: { timeVisible: true, secondsVisible: false, borderColor: '#1f2937' },
      rightPriceScale: { borderColor: '#1f2937' },
      crosshair: { mode: LC.CrosshairMode.Normal },
    });
    var lakhFmt = { type: 'custom', formatter: function (v) { return fmtL(v); }, minMove: 1 };

    var price = chart.addSeries(LC.CandlestickSeries, {
      upColor: '#22c55e', downColor: '#ef4444', borderVisible: false,
      wickUpColor: '#22c55e', wickDownColor: '#ef4444',
      priceFormat: { type: 'price', precision: 1, minMove: 0.1 },
    }, 0);
    function lvl(color, style, title) {
      return chart.addSeries(LC.LineSeries, {
        color: color, lineWidth: 1, lineStyle: style, lineType: LC.LineType.WithSteps,
        lastValueVisible: true, priceLineVisible: false, crosshairMarkerVisible: false,
        title: title, priceFormat: { type: 'price', precision: 0, minMove: 1 },
        // Levels are overlays: they must never stretch the pane's autoscale
        // (a far-away wall step was compressing the candles into a ribbon).
        autoscaleInfoProvider: function () { return null; },
      }, 0);
    }
    var lv = {
      cw: lvl('#ef4444', LC.LineStyle.Solid, 'CW'),
      pw: lvl('#22c55e', LC.LineStyle.Solid, 'PW'),
      cw_oi: lvl('#ef4444', LC.LineStyle.Dotted, 'CWoi'),
      pw_oi: lvl('#22c55e', LC.LineStyle.Dotted, 'PWoi'),
      cross: lvl('#fbbf24', LC.LineStyle.Dashed, '0G'),
    };

    var flow = chart.addSeries(LC.CandlestickSeries, {
      upColor: '#22c55e', downColor: '#ef4444', borderVisible: false,
      wickUpColor: '#22c55e', wickDownColor: '#ef4444',
      priceFormat: lakhFmt,
    }, 1);
    var flowMarkers = null;
    try { flowMarkers = LC.createSeriesMarkers(flow, []); } catch (e) {}

    var nearPE = chart.addSeries(LC.LineSeries, { color: '#22c55e', lineWidth: 2, title: 'nPE', priceLineVisible: false, priceFormat: { type: 'price', precision: 2, minMove: 0.01 } }, 2);
    var nearCE = chart.addSeries(LC.LineSeries, { color: '#ef4444', lineWidth: 2, title: 'nCE', priceLineVisible: false, priceFormat: { type: 'price', precision: 2, minMove: 0.01 } }, 2);
    var wingPE = chart.addSeries(LC.LineSeries, { color: '#22c55e', lineWidth: 2, title: 'wPE', priceLineVisible: false, priceFormat: { type: 'price', precision: 2, minMove: 0.01 } }, 3);
    var wingCE = chart.addSeries(LC.LineSeries, { color: '#ef4444', lineWidth: 2, title: 'wCE', priceLineVisible: false, priceFormat: { type: 'price', precision: 2, minMove: 0.01 } }, 3);

    // stacked volume: total (red) drawn first, PE portion (green) overdrawn
    var volTotal = chart.addSeries(LC.HistogramSeries, { color: '#ef4444', priceFormat: lakhFmt, lastValueVisible: false, priceLineVisible: false }, 4);
    var volPE = chart.addSeries(LC.HistogramSeries, { color: '#22c55e', priceFormat: lakhFmt, lastValueVisible: false, priceLineVisible: false }, 4);

    try {
      var panes = chart.panes();
      var stretch = [3.0, 2.2, 1.0, 1.0, 0.9];
      panes.forEach(function (p, i) { if (stretch[i]) p.setStretchFactor(stretch[i]); });
    } catch (e) {}

    chart.subscribeCrosshairMove(function (param) {
      var ro = document.getElementById('fc-readout');
      if (!ro) return;
      var c = param && param.time != null ? byTimeRef.current[param.time] : null;
      if (!c) { ro.textContent = idleReadout(); return; }
      var md = seriesRef.current && seriesRef.current.mode === 'total' ? 'total' : 'flow';
      var bkt = md === 'flow' ? (c.c - c.o) : (c.c2 - c.o2);
      var txt = c.t + '  ·  spot ' + (c.sc || '--')
        + '  ·  bucket ' + fmtL(bkt) + '  ·  cum ' + fmtL(md === 'flow' ? c.c : c.c2);
      if (c.iv) txt += '  ·  nIV ' + (c.iv.near_pe || '--') + '/' + (c.iv.near_ce || '--')
        + '  ·  wIV ' + (c.iv.wing_pe || '--') + '/' + (c.iv.wing_ce || '--');
      if (c.v_pe != null) txt += '  ·  vol P ' + fmtL(c.v_pe) + ' / C ' + fmtL(c.v_ce || 0);
      if (c.conflict) txt += '  ·  IV CONFLICT';
      ro.textContent = txt;
    });

    chartRef.current = chart;
    seriesRef.current = { price: price, lv: lv, flow: flow, flowMarkers: flowMarkers,
      nearPE: nearPE, nearCE: nearCE, wingPE: wingPE, wingCE: wingCE,
      volTotal: volTotal, volPE: volPE, mode: 'flow', fitted: false };
    return function () {
      try { chart.remove(); } catch (e) {}
      chartRef.current = null; seriesRef.current = null;
    };
  }, []);

  function idleReadout() {
    var lvn = ((dataRef.current || {}).levels_now) || {};
    var sp = liveRef.current.spot;
    function dist(v) { return (v != null && sp) ? ((sp - v) >= 0 ? '+' : '') + (sp - v).toFixed(0) : '--'; }
    return 'live ' + (sp || '--') + '  ·  CW ' + dist(lvn.cw) + '  ·  PW ' + dist(lvn.pw)
      + '  ·  0Γ ' + dist(lvn.cross) + '  ·  scroll = zoom · drag = pan · hover for bucket detail';
  }

  function mergedPriceData() {
    // Kite historical bars (authoritative OHLC) first; snapshot-derived bucket
    // bars only for times AFTER the last historical bar; live update() rides on top.
    var map = {};
    var hist = histRef.current.tf === tf ? histRef.current.bars : [];
    var lastHist = 0;
    hist.forEach(function (b) { map[b.time] = b; lastHist = Math.max(lastHist, b.time); });
    var cds = ((dataRef.current || {}).candles) || [];
    cds.forEach(function (c) {
      if (!c.sc) return;
      var t = bucketTime(c.t);
      if (t > lastHist) map[t] = { time: t, open: c.so, high: c.sh, low: c.sl, close: c.sc };
    });
    return Object.keys(map).map(Number).sort(function (a, b) { return a - b; }).map(function (t) { return map[t]; });
  }

  // Auto-load Kite history on first mount and every timeframe switch; the
  // ⤓ button remains as a manual re-fetch.
  useEffect(function () {
    histRef.current = { tf: null, bars: [] };
    loadHistory();
  }, [tf]);

  // Candle-close reconciliation + gap self-heal: ~8s after each bucket closes,
  // pull today's Kite bars (tick-true) and correct CLOSED bars in place via
  // per-bar update() — never setData — so zoom/scroll and the live
  // forming candle are untouched; missed buckets (laptop sleep) backfill free.
  useEffect(function () {
    var id = setInterval(function () {
      var now = new Date();
      var mins = now.getHours() * 60 + now.getMinutes();
      if (mins < 9 * 60 + 15 || mins > 15 * 60 + 31) return;
      var slotStart = mins - ((mins - (9 * 60 + 15)) % tf);
      var secsIn = (mins - slotStart) * 60 + now.getSeconds();
      var slotKey = tf + ':' + slotStart;
      if (secsIn < 8 || lastFixRef.current === slotKey) return;
      lastFixRef.current = slotKey;
      var curT = bucketTime(('0' + Math.floor(slotStart / 60)).slice(-2) + ':' + ('0' + (slotStart % 60)).slice(-2));
      fetch(API + '/spot/candles?tf=' + tf + '&days=1')
        .then(function (r) { return r.json(); })
        .then(function (d) {
          var bars = (d && d.candles) || [];
          var S = seriesRef.current;
          if (!bars.length || !S) return;
          var closed = bars.filter(function (b) { return b.time < curT; });
          closed.forEach(function (b) { try { S.price.update(b); } catch (e) {} });
          var h = histRef.current;
          if (h.tf === tf && h.bars.length) {
            var mm = {};
            h.bars.forEach(function (b) { mm[b.time] = b; });
            closed.forEach(function (b) { mm[b.time] = b; });
            h.bars = Object.keys(mm).map(Number).sort(function (a, b) { return a - b; }).map(function (t) { return mm[t]; });
          }
        }).catch(function () {});
    }, 5000);
    return function () { clearInterval(id); };
  }, [tf]);

  function zoomToday() {
    // Default view = current session: 09:15 to a little beyond now.
    try {
      var now = new Date();
      var from = Math.floor(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate(), 9, 15) / 1000) - tf * 60;
      var mins = now.getHours() * 60 + now.getMinutes();
      var to = Math.floor(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate(), Math.floor(mins / 60), mins % 60) / 1000) + tf * 60 * 6;
      chartRef.current.timeScale().setVisibleRange({ from: from, to: Math.max(to, from + tf * 60 * 20) });
    } catch (e) {}
  }

  function loadHistory() {
    if (histLoading) return;
    setHistLoading(true);
    fetch(API + '/spot/candles?tf=' + tf + '&days=5')
      .then(function (r) { return r.json(); })
      .then(function (d) {
        if (d && d.candles && d.candles.length) {
          histRef.current = { tf: tf, bars: d.candles };
          var S = seriesRef.current;
          if (S) {
            S.price.setData(mergedPriceData());
            zoomToday();
          }
        }
      }).catch(function () {}).finally(function () { setHistLoading(false); });
  }

  // ── data fetch ──
  useEffect(function () {
    var stop = false;
    function pull() {
      fetch(API + '/oi/candles?tf=' + tf + '&strikes=' + band)
        .then(function (r) { return r.json(); })
        .then(function (d) {
          if (stop || !d || !d.candles) return;
          var lc = d.candles[d.candles.length - 1];
          var lv = liveRef.current;
          if (lc && lc.sc && (!lv.cndl || lv.cndl.t <= lc.t)) lv.cndl = { t: lc.t, o: lc.so, h: lc.sh, l: lc.sl, c: lc.sc };
          setData(d);
        }).catch(function () {});
    }
    pull();
    var id = setInterval(pull, 30000);
    function wake() {
      if (document.hidden) return;
      pull();
      lastFixRef.current = null; // let the reconcile timer refetch today's Kite bars at once
    }
    document.addEventListener('visibilitychange', wake);
    window.addEventListener('focus', wake);
    return function () {
      stop = true; clearInterval(id);
      document.removeEventListener('visibilitychange', wake);
      window.removeEventListener('focus', wake);
    };
  }, [tf, band]);

  // ── apply data to series ──
  useEffect(function () {
    var S = seriesRef.current;
    if (!S || !data || !data.candles) return;
    S.mode = mode;
    var cds = data.candles;
    var map = {};
    var flowD = [], nPE = [], nCE = [], wPE = [], wCE = [], vT = [], vP = [], marks = [];
    var lvD = { cw: [], pw: [], cw_oi: [], pw_oi: [], cross: [] };
    cds.forEach(function (c) {
      var t = bucketTime(c.t);
      map[t] = c;
      var K = mode === 'flow' ? [c.o, c.h, c.l, c.c] : [c.o2, c.h2, c.l2, c.c2];
      flowD.push({ time: t, open: K[0], high: K[1], low: K[2], close: K[3] });
      if (mode === 'flow' && c.conflict) marks.push({ time: t, position: 'aboveBar', color: '#fbbf24', shape: 'circle', size: 0.6 });
      if (c.iv) {
        if (c.iv.near_pe) nPE.push({ time: t, value: c.iv.near_pe });
        if (c.iv.near_ce) nCE.push({ time: t, value: c.iv.near_ce });
        if (c.iv.wing_pe) wPE.push({ time: t, value: c.iv.wing_pe });
        if (c.iv.wing_ce) wCE.push({ time: t, value: c.iv.wing_ce });
      }
      var L = c.lv || {};
      ['cw', 'pw', 'cw_oi', 'pw_oi', 'cross'].forEach(function (k) {
        if (L[k] != null) lvD[k].push({ time: t, value: L[k] });
      });
      var vp = c.v_pe || 0, vc = c.v_ce || 0;
      if (vp + vc > 0) {
        vT.push({ time: t, value: vp + vc });
        vP.push({ time: t, value: vp });
      }
    });
    byTimeRef.current = map;
    S.price.setData(mergedPriceData());
    S.flow.setData(flowD);
    if (S.flowMarkers) { try { S.flowMarkers.setMarkers(marks); } catch (e) {} }
    S.nearPE.setData(nPE); S.nearCE.setData(nCE);
    S.wingPE.setData(wPE); S.wingCE.setData(wCE);
    S.volTotal.setData(vT); S.volPE.setData(vP);
    ['cw', 'pw', 'cw_oi', 'pw_oi', 'cross'].forEach(function (k) { S.lv[k].setData(lvD[k]); });
    if (!S.fitted && flowD.length > 0) {
      zoomToday();
      S.fitted = true;
    }
    var ro = document.getElementById('fc-readout');
    if (ro) ro.textContent = idleReadout();
  }, [data, mode, lots]);

  // ── live SSE → price series only ──
  useEffect(function () {
    var es;
    try { es = new EventSource(API + '/stream/spot'); } catch (e) { return; }
    es.onmessage = function (ev) {
      var m; try { m = JSON.parse(ev.data); } catch (e) { return; }
      if (!m || !m.s) return;
      var lv = liveRef.current;
      lv.spot = m.s;
      var now = new Date();
      var mins = now.getHours() * 60 + now.getMinutes();
      var slotStart = mins - ((mins - (9 * 60 + 15)) % tf);
      var lbl = ('0' + Math.floor(slotStart / 60)).slice(-2) + ':' + ('0' + (slotStart % 60)).slice(-2);
      if (!lv.cndl || lv.cndl.t !== lbl) {
        var prevC = lv.cndl ? lv.cndl.c : m.s;
        lv.cndl = { t: lbl, o: prevC, h: Math.max(prevC, m.s), l: Math.min(prevC, m.s), c: m.s };
      } else {
        lv.cndl.h = Math.max(lv.cndl.h, m.s);
        lv.cndl.l = Math.min(lv.cndl.l, m.s);
        lv.cndl.c = m.s;
      }
      var S = seriesRef.current;
      if (S) {
        try {
          S.price.update({ time: bucketTime(lv.cndl.t), open: lv.cndl.o, high: lv.cndl.h, low: lv.cndl.l, close: lv.cndl.c });
        } catch (e) {}
      }
    };
    return function () { try { es.close(); } catch (e) {} };
  }, [tf]);

  // TV-style countdown to candle close, pinned to the live price's y position.
  useEffect(function () {
    var id = setInterval(function () {
      var S = seriesRef.current;
      var sp = liveRef.current.spot;
      var now = new Date();
      var mins = now.getHours() * 60 + now.getMinutes();
      if (!S || !sp || mins < 9 * 60 + 15 || mins > 15 * 60 + 30) { setCd(null); return; }
      var secsIn = ((mins - (9 * 60 + 15)) % tf) * 60 + now.getSeconds();
      var left = tf * 60 - secsIn;
      var y = null;
      try { y = S.price.priceToCoordinate(sp); } catch (e) {}
      if (y == null || isNaN(y)) { setCd(null); return; }
      setCd({ txt: ('0' + Math.floor(left / 60)).slice(-2) + ':' + ('0' + (left % 60)).slice(-2), y: y });
    }, 1000);
    return function () { clearInterval(id); };
  }, [tf]);

  var btn = function (on) {
    return { fontSize: 11, fontWeight: 700, cursor: 'pointer', padding: '4px 10px', borderRadius: 6, fontFamily: 'inherit',
             background: on ? 'rgba(56,189,248,0.15)' : 'transparent', color: on ? '#38bdf8' : '#9ca3af',
             border: '1px solid ' + (on ? 'rgba(56,189,248,0.5)' : '#1f2937') };
  };
  return (
    <div style={{ background: '#0a0f1c', border: '1px solid #1f2937', borderRadius: 12, padding: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 6 }}>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <button style={btn(mode === 'flow')} onClick={function () { setMode('flow'); }}>Flow ΔPE−ΔCE</button>
          <button style={btn(mode === 'total')} onClick={function () { setMode('total'); }}>Total ΔPE+ΔCE</button>
          <span style={{ width: 10 }} />
          {[3, 5, 15].map(function (t) { return <button key={t} style={btn(tf === t)} onClick={function () { setTf(t); }}>{t}m</button>; })}
          <span style={{ width: 10 }} />
          {[[0, 'All'], [5, '±5'], [10, '±10'], [20, '±20']].map(function (b) {
            return <button key={b[0]} style={btn(band === b[0])} onClick={function () { setBand(b[0]); }}>{b[1]}</button>;
          })}
        </div>
        <div style={{ fontSize: 10, color: '#6b7a8d' }}>
          solid = GEX wall · dotted = max-OI wall · <span style={{ color: '#fbbf24' }}>amber</span> = zero gamma · <span style={{ color: '#fbbf24' }}>○</span> IV conflict
        </div>
      </div>
      <div id="fc-readout" style={{ fontFamily: 'monospace', fontSize: 11, color: '#9ca3af', minHeight: 16, marginBottom: 4 }}>
        Loading…
      </div>
      <div style={{ position: 'relative' }}>
        <div ref={wrapRef} style={{ width: '100%', height: 760 }} />
        {chg20 ? (
          <div style={{ position: 'absolute', top: 34, left: 8, zIndex: 5, pointerEvents: 'none', background: 'rgba(10,15,28,0.88)', border: '1px solid #1f2937', borderRadius: 8, padding: '4px 10px', fontSize: 10, fontFamily: 'monospace' }}>
            <span style={{ color: '#8b98a9', fontWeight: 700 }}>ΔOI </span>
            <span style={{ background: '#16a34a', color: '#ffffff', fontWeight: 700, padding: '1px 7px', borderRadius: 4, marginLeft: 2 }}>C {(chg20.ce > 0 ? '+' : '') + fmtL(chg20.ce)}</span>
            <span style={{ background: '#dc2626', color: '#ffffff', fontWeight: 700, padding: '1px 7px', borderRadius: 4, marginLeft: 4 }}>P {(chg20.pe > 0 ? '+' : '') + fmtL(chg20.pe)}</span>
          </div>
        ) : null}
        {cd ? (
          <div style={{ position: 'absolute', right: 1, top: cd.y + 10, zIndex: 5, pointerEvents: 'none', background: '#1f2937', color: '#e5e7eb', fontSize: 9.5, fontFamily: 'monospace', padding: '1px 6px', borderRadius: 3 }}>{cd.txt}</div>
        ) : null}
        <div style={{ position: 'absolute', top: 6, left: 8, display: 'flex', gap: 4, zIndex: 5 }}>
          <button title="Auto-scale all panes" onClick={function () {
            var S = seriesRef.current; if (!S) return;
            [S.price, S.flow, S.nearPE, S.wingPE, S.volTotal].forEach(function (sr) {
              try { sr.priceScale().applyOptions({ autoScale: true }); } catch (e) {}
            });
          }} style={btn(false)}>A</button>
          <button title="Logarithmic price scale" onClick={function () {
            var S = seriesRef.current; if (!S) return;
            var next = !logScale;
            try { S.price.priceScale().applyOptions({ mode: next ? LC.PriceScaleMode.Logarithmic : LC.PriceScaleMode.Normal }); } catch (e) {}
            setLogScale(next);
          }} style={btn(logScale)}>L</button>
          <button title="Fit all data" onClick={function () {
            try { chartRef.current.timeScale().fitContent(); } catch (e) {}
          }} style={btn(false)}>⤢</button>
          <button title="Zoom in" onClick={function () {
            try { var ts = chartRef.current.timeScale(); var bs = ts.options().barSpacing || 6; ts.applyOptions({ barSpacing: Math.min(60, bs * 1.3) }); } catch (e) {}
          }} style={btn(false)}>+</button>
          <button title="Zoom out" onClick={function () {
            try { var ts = chartRef.current.timeScale(); var bs = ts.options().barSpacing || 6; ts.applyOptions({ barSpacing: Math.max(0.8, bs / 1.3) }); } catch (e) {}
          }} style={btn(false)}>−</button>
          <button title="Load 5 days of NIFTY history from Kite" onClick={loadHistory} style={btn(false)}>
            {histLoading ? 'Loading…' : '⤓ 5d'}
          </button>
        </div>
      </div>
      <div style={{ fontSize: 9, color: '#4b5563', marginTop: 4, textAlign: 'right' }}>
        Panels: NIFTY + GEX levels · OI flow · near IV · 25Δ wing IV · PE/CE volume — charts by{' '}
        <a href="https://www.tradingview.com/" target="_blank" rel="noreferrer" style={{ color: '#6b7a8d' }}>TradingView</a>
      </div>
    </div>
  );
}


function OIAnalysis(props) {
  var [state, setState] = useState({});
  // initialView lets the Workspace register the ladder and the flow candles as
  // two separate panes off one component. Unset (normal page use) it opens on
  // the ladder exactly as before, and the view toggle still works in both.
  var [view, setView] = useState((props && props.initialView) === 'candles' ? 'candles' : 'ladder');
  var [liveSpot, setLiveSpot] = useState(0);
  var [prevClose, setPrevClose] = useState(0);
  var [mode, setMode] = useState('ct');     // ct | c | t
  var [lhov, setLhov] = useState(null);     // ladder hover strike index
  var [windowMin, setWindowMin] = useState(0);   // default: All (full day)
  var [range, setRange] = useState(20);          // default: ±20 strikes
  var [lots, setLots] = useState(false);    // resets to shares each load
  var pollRef = useRef(null);
  var canvasRef = useRef(null);

  function fetchState() {
    var url = API + '/oi/window?mins=' + windowMin;
    fetch(url).then(function (r) { return r.json(); }).then(function (d) {
      if (d && !d.error) setState(d);
    }).catch(function () {});
  }
  useEffect(function () {
    fetchState();
    pollRef.current = setInterval(fetchState, 30000);
    // Background tabs throttle timers; on return, refresh IMMEDIATELY so no
    // stale ladder/panels are ever shown while the backend kept recording.
    function wake() { if (!document.hidden) fetchState(); }
    document.addEventListener('visibilitychange', wake);
    window.addEventListener('focus', wake);
    return function () {
      if (pollRef.current) clearInterval(pollRef.current);
      document.removeEventListener('visibilitychange', wake);
      window.removeEventListener('focus', wake);
    };
  }, [windowMin]);

  useEffect(function () {
    // Push the ±20 default to server config, but NEVER display the POST
    // response: it is computed at the server's default window and would show
    // mismatched data under the selected window. Refetch window-aware instead.
    fetch(API + '/oi/config', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ strike_range: 20 })
    }).then(function () { fetchState(); }).catch(function () {});
  }, []);

  function setRangeCfg(r) {
    setRange(r === 'all' ? 'all' : r);
    fetch(API + '/oi/config', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ strike_range: r })
    }).then(function () { fetchState(); }).catch(function () {});
  }

  var rows = state.rows || [];
  var head = state.headline || {};
  var spot = state.spot || 0;
  var atm = state.atm || 0;
  var maxPain = state.max_pain;
  var recording = !!state.recording;

  // Live header ticker: SSE spot + previous close (last 15m bar before today
  // from Kite history) for change points / percent.
  useEffect(function () {
    var es;
    try { es = new EventSource(API + '/stream/spot'); } catch (e) { return; }
    es.onmessage = function (ev) {
      try { var m = JSON.parse(ev.data); if (m && m.s) setLiveSpot(m.s); } catch (e) {}
    };
    fetch(API + '/spot/candles?tf=15&days=3')
      .then(function (r) { return r.json(); })
      .then(function (d) {
        var bars = (d && d.candles) || [];
        if (!bars.length) return;
        var now = new Date();
        var today0915 = Math.floor(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate(), 9, 15) / 1000);
        var pc = 0;
        bars.forEach(function (b) { if (b.time < today0915) pc = b.close; });
        if (pc) setPrevClose(pc);
      }).catch(function () {});
    return function () { try { es.close(); } catch (e) {} };
  }, []);
  // ΔOI across ±20 strikes of ATM (from the 30s /oi/state feed) for
  // the Flow Candles corner badge — same numbers as the ladder side panel.
  var chg20 = useMemo(function () {
    if (!rows.length || !atm) return null;
    var ce = 0, pe = 0;
    rows.forEach(function (r) {
      if (Math.abs(r.strike - atm) <= 20 * 50) { ce += (r.ce_chg || 0); pe += (r.pe_chg || 0); }
    });
    return { ce: ce, pe: pe };
  }, [rows, atm]);
  var pcr = head.pcr;
  var sentiment = head.sentiment || 'neutral';
  var bullPct = head.bullish_pct != null ? head.bullish_pct : 50;
  var callPct = head.call_pct != null ? head.call_pct : 50;
  var putPct = head.put_pct != null ? head.put_pct : 50;

  var sentColor = sentiment === 'bullish' ? '#22c55e' : sentiment === 'bearish' ? '#ef4444' : '#9ca3af';

  // ── draw main chart on canvas: totals up from a baseline, NEGATIVE OI
  // changes plotted DOWNWARD below it (both sides), auto-fit scale, hover tooltip ──
  useEffect(function () {
    var cv = canvasRef.current;
    if (!cv) return;
    var dpr = window.devicePixelRatio || 1;
    var W = cv.clientWidth, H = cv.clientHeight;
    cv.width = W * dpr; cv.height = H * dpr;
    var ctx = cv.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, W, H);
    if (!rows.length) return;

    var padL = 52, padR = 10, padT = 10, padB = 26;
    var plotW = W - padL - padR, plotH = H - padT - padB;
    var GREEN = '#1f9e5f', RED = '#d8504f', MUC = '#6b7280';

    // value scale — auto-fits the tallest DRAWN extent, including the hollow
    // decrease ghost in 'ct' mode (which reaches oi + |chg|, the pre-unwind
    // level); the old scale only covered current oi and clipped big unwinds.
    var maxUp = 0;
    rows.forEach(function (r) {
      if (mode === 'c') {
        maxUp = Math.max(maxUp, Math.abs(r.ce_chg), Math.abs(r.pe_chg));
      } else {
        maxUp = Math.max(maxUp, r.ce_oi, r.pe_oi);
        if (mode === 'ct') {
          if (r.ce_chg < 0) maxUp = Math.max(maxUp, r.ce_oi - r.ce_chg);
          if (r.pe_chg < 0) maxUp = Math.max(maxUp, r.pe_oi - r.pe_chg);
        }
      }
    });
    if (maxUp <= 0) maxUp = 1;
    var y0 = padT + plotH;                                 // baseline at bottom
    function yUp(v) { return y0 - (v / maxUp) * plotH; }

    var n = rows.length;
    var groupW = plotW / n;
    var barW = Math.min(14, groupW * 0.36);
    var gap = 2;

    // gridlines + reference labels (up region)
    ctx.strokeStyle = '#1f2937'; ctx.fillStyle = MUC;
    ctx.font = '9px sans-serif'; ctx.textAlign = 'right';
    for (var g = 1; g <= 4; g++) {
      var gv = maxUp * g / 4, gy = yUp(gv);
      ctx.beginPath(); ctx.moveTo(padL, gy); ctx.lineTo(W - padR, gy); ctx.stroke();
      ctx.fillText(fmt(gv, lots), padL - 4, gy + 3);
    }
    ctx.fillText('0', padL - 4, y0 + 3);

    function hatch(x, yTop, w, h, color) {
      if (h <= 0) return;
      ctx.save();
      ctx.beginPath(); ctx.rect(x, yTop, w, h); ctx.clip();
      ctx.strokeStyle = color; ctx.lineWidth = 1;
      for (var i = -h; i < w; i += 4) {
        ctx.beginPath(); ctx.moveTo(x + i, yTop + h); ctx.lineTo(x + i + h, yTop); ctx.stroke();
      }
      ctx.restore();
    }

    ctx.textAlign = 'center';
    rows.forEach(function (r, i) {
      var cx = padL + groupW * i + groupW / 2;
      var ceX = cx - barW - gap / 2;
      var peX = cx + gap / 2;

      function upBar(x, v, col) {
        if (v <= 0) return;
        var ty = yUp(v);
        ctx.fillStyle = col; ctx.fillRect(x, ty, barW, y0 - ty);
      }

      if (mode === 'c') {
        // signed change: solid if increase, hollow outline if decrease (upward, as before)
        if (r.ce_chg >= 0) upBar(ceX, r.ce_chg, GREEN);
        else { ctx.strokeStyle = GREEN; ctx.strokeRect(ceX, yUp(-r.ce_chg), barW, y0 - yUp(-r.ce_chg)); }
        if (r.pe_chg >= 0) upBar(peX, r.pe_chg, RED);
        else { ctx.strokeStyle = RED; ctx.strokeRect(peX, yUp(-r.pe_chg), barW, y0 - yUp(-r.pe_chg)); }
      } else {
        upBar(ceX, r.ce_oi, GREEN);
        upBar(peX, r.pe_oi, RED);
        if (mode === 'ct') {
          if (r.ce_chg > 0) hatch(ceX, yUp(r.ce_oi), barW, yUp(r.ce_oi - r.ce_chg) - yUp(r.ce_oi), '#0c5c34');
          else if (r.ce_chg < 0) { ctx.strokeStyle = '#7fd6a6'; ctx.lineWidth = 1; ctx.strokeRect(ceX, yUp(r.ce_oi - r.ce_chg), barW, yUp(r.ce_oi) - yUp(r.ce_oi - r.ce_chg)); }
          if (r.pe_chg > 0) hatch(peX, yUp(r.pe_oi), barW, yUp(r.pe_oi - r.pe_chg) - yUp(r.pe_oi), '#7a2424');
          else if (r.pe_chg < 0) { ctx.strokeStyle = '#e89a9a'; ctx.lineWidth = 1; ctx.strokeRect(peX, yUp(r.pe_oi - r.pe_chg), barW, yUp(r.pe_oi) - yUp(r.pe_oi - r.pe_chg)); }
        }
      }

      if (n <= 16 || i % 2 === 0) {
        ctx.fillStyle = (r.strike === atm) ? '#38bdf8' : MUC;
        ctx.fillText(r.strike, cx, H - 8);
      }
    });

    // spot + max pain vertical markers
    function markerX(strike) {
      var idx = rows.findIndex(function (r) { return r.strike >= strike; });
      if (idx < 0) idx = rows.length - 1;
      return padL + groupW * idx + groupW / 2;
    }
    if (spot) {
      var sx = markerX(Math.round(spot / 50) * 50);
      ctx.strokeStyle = '#38bdf8'; ctx.setLineDash([4, 3]); ctx.lineWidth = 1;
      ctx.beginPath(); ctx.moveTo(sx, padT); ctx.lineTo(sx, padT + plotH); ctx.stroke();
      ctx.setLineDash([]);
    }
    if (maxPain) {
      var mx = markerX(maxPain);
      ctx.strokeStyle = '#fb923c'; ctx.setLineDash([4, 3]); ctx.lineWidth = 1;
      ctx.beginPath(); ctx.moveTo(mx, padT); ctx.lineTo(mx, padT + plotH); ctx.stroke();
      ctx.setLineDash([]);
    }

    // hover: column highlight + tooltip (totals AND changes, both sides)
    if (lhov != null && lhov >= 0 && lhov < n) {
      var hr = rows[lhov];
      var hx = padL + groupW * lhov;
      ctx.fillStyle = 'rgba(148,163,184,0.08)';
      ctx.fillRect(hx, padT, groupW, plotH);

      function pctOf(chg, oi) {
        var base = oi - chg;
        return base > 0 ? ' (' + (chg >= 0 ? '+' : '') + Math.round(chg / base * 100) + '%)' : '';
      }
      var lines = [
        ['Strike ' + hr.strike + (hr.strike === atm ? '  · ATM' : ''), '#e5e7eb'],
        ['Call OI  ' + fmt(hr.ce_oi, lots), '#7fd6a6'],
        ['Call Δ   ' + (hr.ce_chg >= 0 ? '+' : '') + fmt(hr.ce_chg, lots) + pctOf(hr.ce_chg, hr.ce_oi), hr.ce_chg >= 0 ? '#7fd6a6' : '#fbbf24'],
        ['Put OI   ' + fmt(hr.pe_oi, lots), '#e89a9a'],
        ['Put Δ    ' + (hr.pe_chg >= 0 ? '+' : '') + fmt(hr.pe_chg, lots) + pctOf(hr.pe_chg, hr.pe_oi), hr.pe_chg >= 0 ? '#e89a9a' : '#fbbf24'],
      ];
      ctx.font = '11px monospace'; ctx.textAlign = 'left';
      var tw = 0;
      lines.forEach(function (L) { tw = Math.max(tw, ctx.measureText(L[0]).width); });
      var boxW = tw + 20, boxH = lines.length * 16 + 12;
      var bx = hx + groupW + 8;
      if (bx + boxW > W - padR) bx = hx - boxW - 8;
      var by = padT + 8;
      ctx.fillStyle = 'rgba(10,15,28,0.95)';
      ctx.strokeStyle = '#334155';
      ctx.fillRect(bx, by, boxW, boxH); ctx.strokeRect(bx, by, boxW, boxH);
      lines.forEach(function (L, li) {
        ctx.fillStyle = L[1];
        ctx.fillText(L[0], bx + 10, by + 20 + li * 16);
      });
      ctx.font = '9px sans-serif'; ctx.textAlign = 'center';
    }
  }, [rows, mode, lots, spot, maxPain, atm, view, lhov]);

  function Donut({ pct, color, center, sub }) {
    var R = 46, sw = 11, C = 2 * Math.PI * R;
    return (
      <div style={{ position: 'relative', width: 110, height: 110, margin: '0 auto' }}>
        <svg width="110" height="110" viewBox="0 0 110 110">
          <circle cx="55" cy="55" r={R} fill="none" stroke="#1f2937" strokeWidth={sw} />
          <circle cx="55" cy="55" r={R} fill="none" stroke={color} strokeWidth={sw}
            strokeDasharray={(C * pct / 100) + ' ' + C} strokeLinecap="round"
            transform="rotate(-90 55 55)" />
        </svg>
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          {center}{sub}
        </div>
      </div>
    );
  }

  function MiniBars({ ce, pe, lots, signed }) {
    var mx = Math.max(Math.abs(ce), Math.abs(pe), 1);
    if (!signed) {
      return (
        <div style={{ display: 'flex', gap: 20, alignItems: 'flex-end', justifyContent: 'center', height: 110 }}>
          {[['CALL', ce, '#1f9e5f'], ['PUT', pe, '#d8504f']].map(function (b, i) {
            return (
              <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                <span style={{ fontSize: 11, color: b[2], fontWeight: 500 }}>{fmt(b[1], lots)}</span>
                <div style={{ width: 38, height: Math.max(4, Math.abs(b[1]) / mx * 78), background: b[2], borderRadius: '4px 4px 0 0' }}></div>
                <span style={{ fontSize: 10, color: '#9ca3af' }}>{b[0]}</span>
              </div>
            );
          })}
        </div>
      );
    }
    // signed: bars grow up for builds, hang DOWN from the baseline for unwinds
    var half = 38;
    return (
      <div style={{ position: 'relative', height: 110 }}>
        <div style={{ position: 'absolute', left: '8%', right: '8%', top: 52, borderTop: '1px solid #374151' }}></div>
        <div style={{ display: 'flex', gap: 20, justifyContent: 'center', height: '100%' }}>
          {[['CALL', ce, '#1f9e5f'], ['PUT', pe, '#d8504f']].map(function (b, i) {
            var v = b[1], up = v >= 0;
            var h = Math.max(4, Math.abs(v) / mx * half);
            return (
              <div key={i} style={{ position: 'relative', width: 38 }}>
                <span style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', top: up ? (52 - h - 16) : 34, fontSize: 11, color: b[2], fontWeight: 500, whiteSpace: 'nowrap' }}>{fmtSigned(v, lots)}</span>
                <div style={{ position: 'absolute', left: 0, width: 38, background: b[2], top: up ? (52 - h) : 53, height: h, borderRadius: up ? '4px 4px 0 0' : '0 0 4px 4px' }}></div>
                <span style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', bottom: 0, fontSize: 10, color: '#9ca3af' }}>{b[0]}</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: 16, color: '#e5e7eb', minHeight: 'calc(100vh - 80px)' }}>
      {/* header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
          <span style={{ fontSize: 18, fontWeight: 700 }}>Open Interest</span>
          <span style={{ fontSize: 13, color: '#9ca3af' }}>NIFTY <span style={{ color: '#e5e7eb', fontWeight: 700 }}>{(liveSpot || spot) ? (liveSpot || spot).toFixed(2) : '--'}</span>{(liveSpot || spot) && prevClose ? (function () {
            var pts = (liveSpot || spot) - prevClose;
            var pct = pts / prevClose * 100;
            var up = pts >= 0;
            return <span style={{ color: up ? '#22c55e' : '#ef4444', fontWeight: 700, fontSize: 12, marginLeft: 6 }}>{(up ? '▲ +' : '▼ ') + pts.toFixed(1) + ' (' + (up ? '+' : '') + pct.toFixed(2) + '%)'}</span>;
          })() : null} {state.expiry_label ? '· ' + state.expiry_label : ''}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ display: 'inline-flex', gap: 4 }}>
            <button onClick={function () { setView('ladder'); }} style={btn(view === 'ladder')}>OI Ladder</button>
            <button onClick={function () { setView('candles'); }} style={btn(view === 'candles')}>Flow Candles</button>
          </span>
          <span style={{ fontSize: 11, color: '#9ca3af' }}>max pain <span style={{ color: '#fb923c', fontWeight: 700 }}>{maxPain || '--'}</span></span>
          <span style={{ fontSize: 11, color: '#9ca3af' }}>{state.last_compute_time || '--'}</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 9px', borderRadius: 6, background: recording ? 'rgba(239,68,68,0.12)' : 'rgba(107,114,128,0.12)', border: '1px solid ' + (recording ? 'rgba(239,68,68,0.3)' : '#1f2937') }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: recording ? '#ef4444' : '#6b7280', animation: recording ? 'oipulse 1.4s infinite' : 'none', display: 'inline-block' }}></span>
            <span style={{ color: recording ? '#f87171' : '#6b7280', fontWeight: 700, fontSize: 10 }}>{recording ? 'REC' : 'IDLE'} {'·'} 30s</span>
          </span>
        </div>
      </div>

      {view === 'candles' ? <FlowCandles lots={lots} chg20={chg20} /> : <>
      {/* mode + show lot + range */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        {[['ct', 'OI Change+Total'], ['c', 'OI Change'], ['t', 'Total OI']].map(function (m) {
          var on = mode === m[0];
          return <button key={m[0]} onClick={function () { setMode(m[0]); }} style={btn(on)}>{m[1]}</button>;
        })}
        <span style={{ flex: 1 }}></span>
        <span style={{ fontSize: 11, color: '#9ca3af' }}>strikes</span>
        {RANGES.map(function (rg) {
          var on = (range === rg.r) || (range === 'all' && rg.r === 'all');
          return <button key={rg.r} onClick={function () { setRangeCfg(rg.r); }} style={btn(on)}>{rg.label}</button>;
        })}
        <label style={{ fontSize: 11, color: '#9ca3af', display: 'flex', alignItems: 'center', gap: 5, marginLeft: 6 }}>
          show lot <input type="checkbox" checked={lots} onChange={function (e) { setLots(e.target.checked); }} style={{ accentColor: '#3b82f6' }} />
        </label>
      </div>

      {/* main chart */}
      <div style={{ position: 'relative', width: '100%', height: 300, background: '#0a0f1c', border: '1px solid #1f2937', borderRadius: 10, padding: 8 }}>
        <canvas ref={canvasRef} style={{ width: '100%', height: '100%' }}
          onMouseMove={function (e) {
            var cv = canvasRef.current; if (!cv || !rows.length) return;
            var rc = cv.getBoundingClientRect();
            var gw = (rc.width - 52 - 10) / rows.length;
            var i = Math.floor((e.clientX - rc.left - 52) / gw);
            setLhov(i >= 0 && i < rows.length ? i : null);
          }}
          onMouseLeave={function () { setLhov(null); }}></canvas>
      </div>

      {/* lookback windows */}
      <div style={{ display: 'flex', gap: 5, margin: '10px 0 16px', flexWrap: 'wrap', justifyContent: 'center' }}>
        {WINDOWS.map(function (wd) {
          var on = windowMin === wd.w;
          return <button key={wd.w} onClick={function () { setWindowMin(wd.w); }} style={btn(on)}>{wd.label}</button>;
        })}
      </div>

      {/* panels */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.1fr 1fr 1fr 1fr', gap: 10 }}>
        <div style={card()}>
          <div style={cardTitle()}>Market Sentiment <span style={{ fontSize: 10 }}>(OI)</span></div>
          <Donut pct={sentiment === 'bullish' ? bullPct : sentiment === 'bearish' ? (100 - bullPct) : 50} color={sentColor}
            center={<span style={{ fontSize: 15, fontWeight: 700, color: sentColor, textTransform: 'capitalize' }}>{sentiment}</span>}
            sub={<span style={{ fontSize: 11, color: '#9ca3af' }}>{sentiment === 'bullish' ? bullPct : sentiment === 'bearish' ? (100 - bullPct) : 50}%</span>} />
          <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px solid #1f2937', fontSize: 11, color: '#9ca3af', textAlign: 'center' }}>
            PCR <span style={{ color: '#e5e7eb', fontWeight: 700 }}>{pcr != null ? pcr : '--'}</span> {'·'} {'Δ'}PCR <span style={{ color: '#e5e7eb', fontWeight: 700 }}>{head.pcr_chg != null ? head.pcr_chg : '--'}</span>
          </div>
        </div>

        <div style={card()}>
          <div style={cardTitle()}>OI Change</div>
          <MiniBars ce={head.total_ce_chg} pe={head.total_pe_chg} lots={lots} signed={true} />
        </div>

        <div style={card()}>
          <div style={cardTitle()}>Total OI</div>
          <MiniBars ce={head.total_ce_oi} pe={head.total_pe_oi} lots={lots} />
        </div>

        <div style={card()}>
          <div style={cardTitle()}>Put/Call Ratio</div>
          <Donut pct={callPct} color="#1f9e5f"
            center={<span style={{ fontSize: 10, color: '#9ca3af' }}>PCR</span>}
            sub={<span style={{ fontSize: 17, fontWeight: 700 }}>{pcr != null ? pcr : '--'}</span>} />
          <div style={{ display: 'flex', justifyContent: 'space-around', fontSize: 10, marginTop: 8 }}>
            <span style={{ color: '#f87171' }}>{putPct}% Put</span><span style={{ color: '#4ade80' }}>{callPct}% Call</span>
          </div>
        </div>
      </div>

      {/* insight */}
      <div style={{ marginTop: 12, background: 'rgba(56,189,248,0.06)', border: '1px solid rgba(56,189,248,0.2)', borderRadius: 10, padding: '10px 12px' }}>
        <div style={{ fontSize: 12, color: '#38bdf8', fontWeight: 700, marginBottom: 3 }}>Market Insight</div>
        <div style={{ fontSize: 11.5, color: '#cbd5e1', lineHeight: 1.5 }}>{head.insight || 'Awaiting OI data.'}</div>
      </div>
      </>}

      <style>{'@keyframes oipulse{0%,100%{opacity:1}50%{opacity:0.35}}'}</style>
    </div>
  );
}

function btn(on) {
  return {
    fontSize: 11, padding: '5px 11px', border: '1px solid ' + (on ? 'rgba(56,189,248,0.3)' : '#1f2937'),
    borderRadius: 6, background: on ? 'rgba(56,189,248,0.18)' : 'transparent',
    color: on ? '#38bdf8' : '#9ca3af', fontWeight: on ? 700 : 400, cursor: 'pointer',
  };
}
function card() { return { background: '#0a0f1c', border: '1px solid #1f2937', borderRadius: 10, padding: 12 }; }
function cardTitle() { return { fontSize: 12, color: '#9ca3af', marginBottom: 8 }; }

export default OIAnalysis;
